<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>Subject Titles</h1>
        <Button label="Add Subject Title" icon="pi pi-plus" @click="openAdd" />
      </div>

      <DataTable :value="items" :paginator="true" :rows="10" :loading="loading" stripedRows showGridlines v-model:filters="filters" filterDisplay="menu">
        <template #header>
          <div class="table-header">
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
          </div>
        </template>
        <Column field="name" header="Subject Title" sortable />
        <Column header="Actions" style="width: 10rem">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" @click="openEdit(data)" />
              <Button icon="pi pi-trash" severity="danger" size="small" @click="deleteItem(data.id)" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="showModal" :header="isEditing ? 'Edit Subject Title' : 'Add Subject Title'" :modal="true" :style="{width: '950px'}" :maximizable="true">
      <div class="form-group" style="padding: 1rem 0;">
        <FloatLabel variant="in">
          <InputText id="name" v-model="form.name" class="w-full" />
          <label for="name">Subject Title *</label>
        </FloatLabel>
      </div>

      <!-- Legislative Status Sections -->
      <div v-if="isEditing" class="legis-status-section">
        <h3 class="legis-title">Legislative Status</h3>

        <div class="legis-grid">
            <!-- House -->
            <div class="legis-card">
              <div class="legis-card-header">
                <span class="legis-card-title">House</span>
                <Button icon="pi pi-plus" label="Add" size="small" severity="success" @click="addStatus('house')" />
              </div>
              <DataTable :value="getStatusesBySection('house')" showGridlines size="small" v-if="getStatusesBySection('house').length">
                <Column field="status" header="Status" style="min-width: 180px">
                  <template #body="{ data }">
                    <Select v-model="data.status" :options="houseStatusOptions" optionLabel="label" optionValue="value" class="w-full" placeholder="Select status" />
                  </template>
                </Column>
                <Column field="date" header="Date" style="min-width: 130px">
                  <template #body="{ data }">
                    <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
                  </template>
                </Column>
                <Column field="bill_no" header="Bill No." style="min-width: 120px">
                  <template #body="{ data }">
                    <InputText v-model="data.bill_no" class="w-full" placeholder="Bill No." />
                  </template>
                </Column>
                <Column field="drive_link" header="Drive Link" style="min-width: 150px">
                  <template #body="{ data }">
                    <InputText v-model="data.drive_link" class="w-full" placeholder="Link" />
                  </template>
                </Column>
                <Column style="width: 3rem">
                  <template #body="{ index }">
                    <Button icon="pi pi-trash" severity="danger" size="small" text @click="removeStatus('house', index)" />
                  </template>
                </Column>
              </DataTable>
              <div v-else class="legis-card-empty">No entries yet. Click "+ Add" to begin.</div>
            </div>

            <!-- Senate -->
            <div class="legis-card">
              <div class="legis-card-header">
                <span class="legis-card-title">Senate</span>
                <Button icon="pi pi-plus" label="Add" size="small" severity="success" @click="addStatus('senate')" />
              </div>
              <DataTable :value="getStatusesBySection('senate')" showGridlines size="small" v-if="getStatusesBySection('senate').length">
                <Column field="status" header="Status" style="min-width: 180px">
                  <template #body="{ data }">
                    <Select v-model="data.status" :options="houseStatusOptions" optionLabel="label" optionValue="value" class="w-full" placeholder="Select status" />
                  </template>
                </Column>
                <Column field="date" header="Date" style="min-width: 130px">
                  <template #body="{ data }">
                    <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
                  </template>
                </Column>
                <Column field="bill_no" header="Bill No." style="min-width: 120px">
                  <template #body="{ data }">
                    <InputText v-model="data.bill_no" class="w-full" placeholder="Bill No." />
                  </template>
                </Column>
                <Column field="drive_link" header="Drive Link" style="min-width: 150px">
                  <template #body="{ data }">
                    <InputText v-model="data.drive_link" class="w-full" placeholder="Link" />
                  </template>
                </Column>
                <Column style="width: 3rem">
                  <template #body="{ index }">
                    <Button icon="pi pi-trash" severity="danger" size="small" text @click="removeStatus('senate', index)" />
                  </template>
                </Column>
              </DataTable>
            </div>

            <!-- Bicameral Conference -->
            <div class="legis-card">
              <div class="legis-card-header">
                <span class="legis-card-title">Bicameral Conference</span>
                <Button icon="pi pi-plus" label="Add" size="small" severity="success" @click="addStatus('bicameral')" />
              </div>
              <DataTable :value="getStatusesBySection('bicameral')" showGridlines size="small" v-if="getStatusesBySection('bicameral').length">
                <Column field="status" header="Status" style="min-width: 180px">
                  <template #body="{ data }">
                    <Select v-model="data.status" :options="bicameralStatusOptions" optionLabel="label" optionValue="value" class="w-full" placeholder="Select status" />
                  </template>
                </Column>
                <Column field="date" header="Date" style="min-width: 130px">
                  <template #body="{ data }">
                    <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
                  </template>
                </Column>
                <Column field="bill_no" header="Bill No." style="min-width: 120px">
                  <template #body="{ data }">
                    <InputText v-model="data.bill_no" class="w-full" placeholder="Bill No." />
                  </template>
                </Column>
                <Column field="drive_link" header="Drive Link" style="min-width: 150px">
                  <template #body="{ data }">
                    <InputText v-model="data.drive_link" class="w-full" placeholder="Link" />
                  </template>
                </Column>
                <Column style="width: 3rem">
                  <template #body="{ index }">
                    <Button icon="pi pi-trash" severity="danger" size="small" text @click="removeStatus('bicameral', index)" />
                  </template>
                </Column>
              </DataTable>
              <div v-if="!getStatusesBySection('bicameral').length" class="legis-card-empty">No entries yet. Click "+ Add" to begin.</div>
            </div>

            <!-- Enrollment -->
            <div class="legis-card">
              <div class="legis-card-header">
                <span class="legis-card-title">Enrollment</span>
                <Button icon="pi pi-plus" label="Add" size="small" severity="success" @click="addStatus('enrollment')" />
              </div>
              <DataTable :value="getStatusesBySection('enrollment')" showGridlines size="small" v-if="getStatusesBySection('enrollment').length">
                <Column field="status" header="Status" style="min-width: 180px">
                  <template #body="{ data }">
                    <Select v-model="data.status" :options="enrollmentStatusOptions" optionLabel="label" optionValue="value" class="w-full" placeholder="Select status" />
                  </template>
                </Column>
                <Column field="date" header="Date" style="min-width: 130px">
                  <template #body="{ data }">
                    <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
                  </template>
                </Column>
                <Column field="bill_no" header="Bill No." style="min-width: 120px">
                  <template #body="{ data }">
                    <InputText v-model="data.bill_no" class="w-full" placeholder="Bill No." />
                  </template>
                </Column>
                <Column field="drive_link" header="Drive Link" style="min-width: 150px">
                  <template #body="{ data }">
                    <InputText v-model="data.drive_link" class="w-full" placeholder="Link" />
                  </template>
                </Column>
                <Column style="width: 3rem">
                  <template #body="{ index }">
                    <Button icon="pi pi-trash" severity="danger" size="small" text @click="removeStatus('enrollment', index)" />
                  </template>
                </Column>
              </DataTable>
              <div v-if="!getStatusesBySection('enrollment').length" class="legis-card-empty">No entries yet. Click "+ Add" to begin.</div>
            </div>

            <!-- Enactment -->
            <div class="legis-card">
              <div class="legis-card-header">
                <span class="legis-card-title">Enactment</span>
                <Button icon="pi pi-plus" label="Add" size="small" severity="success" @click="addStatus('enactment')" />
              </div>
              <DataTable :value="getStatusesBySection('enactment')" showGridlines size="small" v-if="getStatusesBySection('enactment').length">
                <Column field="status" header="Status" style="min-width: 180px">
                  <template #body="{ data }">
                    <Select v-model="data.status" :options="enactmentStatusOptions" optionLabel="label" optionValue="value" class="w-full" placeholder="Select status" />
                  </template>
                </Column>
                <Column field="date" header="Date" style="min-width: 130px">
                  <template #body="{ data }">
                    <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
                  </template>
                </Column>
                <Column field="bill_no" header="Bill No." style="min-width: 120px">
                  <template #body="{ data }">
                    <InputText v-model="data.bill_no" class="w-full" placeholder="Bill No." />
                  </template>
                </Column>
                <Column field="drive_link" header="Drive Link" style="min-width: 150px">
                  <template #body="{ data }">
                    <InputText v-model="data.drive_link" class="w-full" placeholder="Link" />
                  </template>
                </Column>
                <Column style="width: 3rem">
                  <template #body="{ index }">
                    <Button icon="pi pi-trash" severity="danger" size="small" text @click="removeStatus('enactment', index)" />
                  </template>
                </Column>
              </DataTable>
              <div v-if="!getStatusesBySection('enactment').length" class="legis-card-empty">No entries yet. Click "+ Add" to begin.</div>
            </div>
        </div>
      </div>

      <div class="modal-actions">
        <Button label="Cancel" severity="secondary" icon="pi pi-times" @click="showModal = false" />
        <Button :label="isEditing ? 'Update' : 'Create'" icon="pi pi-check" :loading="saving" @click="save" />
      </div>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'

const items = ref([])
const loading = ref(false)
const showModal = ref(false)
const isEditing = ref(false)
const saving = ref(false)
const form = ref({ id: null, name: '', legislative_statuses: [] })
const filters = ref({ global: { value: null, matchMode: 'contains' } })

const houseStatusOptions = [
  { label: 'Pending at the Committee Level', value: 'Pending at the Committee Level' },
  { label: 'Committee Report', value: 'Committee Report' },
  { label: 'Approved on 2nd Reading', value: 'Approved on 2nd Reading' },
  { label: 'Approved on 3rd Reading', value: 'Approved on 3rd Reading' },
]

const bicameralStatusOptions = [
  { label: 'Consolidated', value: 'Consolidated' },
  { label: 'Bicameral Conference', value: 'Bicameral Conference' },
  { label: 'Ratified', value: 'Ratified' },
]

const enrollmentStatusOptions = [
  { label: 'Enrolled', value: 'Enrolled' },
]

const enactmentStatusOptions = [
  { label: 'Signed into Law', value: 'Signed into Law' },
  { label: 'Vetoed', value: 'Vetoed' },
]

const getStatusesBySection = (section) => {
  return form.value.legislative_statuses.filter(s => s.section === section)
}

const addStatus = (section) => {
  form.value.legislative_statuses.push({ section, status: '', date: null, bill_no: '', drive_link: '' })
}

const removeStatus = (section, index) => {
  const sectionItems = form.value.legislative_statuses.filter(s => s.section === section)
  const item = sectionItems[index]
  const globalIndex = form.value.legislative_statuses.indexOf(item)
  if (globalIndex !== -1) form.value.legislative_statuses.splice(globalIndex, 1)
}

const load = async () => {
  loading.value = true
  try {
    const res = await api.get('/subject-titles')
    items.value = res.data
  } catch (e) { console.error(e) }
  finally { loading.value = false }
}

const openAdd = () => {
  isEditing.value = false
  form.value = { id: null, name: '', legislative_statuses: [] }
  showModal.value = true
}

const openEdit = (item) => {
  isEditing.value = true
  form.value = {
    ...item,
    legislative_statuses: (item.statuses || []).map(s => ({
      id: s.id,
      section: s.section || 'house',
      status: s.status,
      date: s.date,
      bill_no: s.bill_no || '',
      drive_link: s.drive_link || '',
    }))
  }
  showModal.value = true
}

const save = async () => {
  if (!form.value.name) return
  saving.value = true
  try {
    const payload = {
      name: form.value.name,
      legislative_statuses: form.value.legislative_statuses || []
    }
    if (isEditing.value) {
      await api.put(`/subject-titles/${form.value.id}`, payload)
    } else {
      await api.post('/subject-titles', payload)
    }
    showModal.value = false
    load()
  } catch (e) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally { saving.value = false }
}

const deleteItem = async (id) => {
  const result = await Swal.fire({ title: 'Delete?', text: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#ef4444' })
  if (result.isConfirmed) {
    try {
      await api.delete(`/subject-titles/${id}`)
      load()
      Swal.fire({ title: 'Deleted!', text: 'Subject title deleted.', icon: 'success', timer: 1500, showConfirmButton: false })
    } catch (e) {
      const msg = e.response?.data?.message || 'Failed to delete'
      Swal.fire({ title: 'Cannot Delete', text: msg, icon: 'error' })
    }
  }
}

onMounted(load)
</script>

<style scoped>
.page { padding: 1rem 0; background: var(--p-surface-50); min-height: 100vh; }
.container { margin: 1rem; padding: 1.5rem; background: white; border-radius: 12px; box-shadow: 0 2px 12px rgba(30, 58, 138, 0.06); }
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 2px solid #1e3a8a; }
.page-header h1 { color: #1e3a8a; font-size: 2rem; font-weight: 700; }
.table-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.actions { display: flex; gap: 0.5rem; }
.form-group .p-inputtext { width: 100% !important; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.75rem; padding: 1.25rem 0 0; margin-top: 1.5rem; border-top: 1px solid #e5e7eb; }

.legis-status-section { margin-bottom: 1rem; }
.legis-title { color: #1e3a8a; font-size: 1rem; font-weight: 700; margin-bottom: 1.25rem; padding: 0.6rem 1rem; background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); border-left: 4px solid #1e3a8a; border-radius: 6px; }
.legis-grid { display: flex; flex-direction: column; gap: 1.25rem; }
.legis-col { display: flex; flex-direction: column; gap: 1.25rem; }

.legis-card { border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; background: white; box-shadow: 0 1px 4px rgba(0,0,0,0.04); transition: box-shadow 0.2s; }
.legis-card:hover { box-shadow: 0 4px 12px rgba(30, 58, 138, 0.08); }
.legis-card-header { display: flex; justify-content: space-between; align-items: center; padding: 0.7rem 1rem; background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-bottom: 1px solid #e2e8f0; }
.legis-card-title { font-weight: 700; font-size: 0.95rem; color: #1e3a8a; }

.legis-card-empty { padding: 1.5rem; text-align: center; color: #94a3b8; font-size: 0.85rem; font-style: italic; }

:deep(.p-datatable .p-select),
:deep(.p-datatable .p-datepicker),
:deep(.p-datatable .p-inputtext) { width: 100% !important; }

:deep(.p-datatable-sm .p-datatable-tbody > tr > td) { padding: 0.4rem 0.5rem; }
:deep(.p-datatable-sm .p-datatable-thead > tr > th) { padding: 0.5rem; font-size: 0.8rem; background: #f8fafc; color: #475569; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; }

@media (max-width: 768px) {
  .legis-grid { grid-template-columns: 1fr; }
}
</style>
